import { addTokenHeader, BusinessAxiosConfig, RequestConfig } from '@src/config/AxiosConfig';
import { BUSINESS_API_VERSION } from '@src/constants';
import ClientApi from '@src/types/client';
import { getState } from '@src/utils';
import { AxiosResponse } from 'axios';

export const getSettlementAccountsService = (
  payload: ClientApi.User.GetSettlementAccounts.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetSettlementAccounts.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetSettlementAccounts.Response,
    AxiosResponse<ClientApi.User.GetSettlementAccounts.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/User/SettlementAccounts?PageSize=${payload.pageSize}&PageNumber=${payload.pageNumber}`,
    { ...config, ...configWithToken }
  ).then((res) => res.data?.data);
};

export const addSettlementAccountService = async (
  payload: ClientApi.User.AddSettlementAccounts.Request,
  config?: RequestConfig
): Promise<ClientApi.User.AddSettlementAccounts.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.User.AddSettlementAccounts.Response,
    AxiosResponse<ClientApi.User.AddSettlementAccounts.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/AddSettlementAccount`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const updateSettlementAccountService = async (
  payload: ClientApi.User.UpdateSettlementAccount.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UpdateSettlementAccount.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.User.UpdateSettlementAccount.Response,
    AxiosResponse<ClientApi.User.UpdateSettlementAccount.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UpdateSettlementAccount`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const deleteSettlementAccountService = async (
  payload: ClientApi.User.DeleteSettlementAccount.Request,
  config?: RequestConfig
): Promise<ClientApi.User.DeleteSettlementAccount.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.delete<
    ClientApi.User.DeleteSettlementAccount.Response,
    AxiosResponse<ClientApi.User.DeleteSettlementAccount.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/DeleteSettlementAccount?AccountId=${payload.AccountId}`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const getPrimarySettlementAccountsService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetPrimarySettlementAccounts.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetPrimarySettlementAccounts.Response,
    AxiosResponse<ClientApi.User.GetPrimarySettlementAccounts.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/PrimarySettlementAccounts`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const addPaymentLinkService = async (
  payload: ClientApi.User.AddPaymentLink.Request,
  config?: RequestConfig
): Promise<ClientApi.User.AddPaymentLink.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.User.AddPaymentLink.Response,
    AxiosResponse<ClientApi.User.AddPaymentLink.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/AddPaymentLink`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const getPaymentLinksService = async (
  payload: ClientApi.User.GetPaymentLinks.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetPaymentLinks.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload?.startDate,
      endDate: payload?.endDate
    },
    searchValue: payload.searchValue,
    status: payload.status,
    frequency: payload.frequency
  };

  return BusinessAxiosConfig.post<
    ClientApi.User.GetPaymentLinks.Response,
    AxiosResponse<ClientApi.User.GetPaymentLinks.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/PaymentLinks`, payload2, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const updatePaymentLinkStatusService = async (
  payload: ClientApi.User.UpdatePaymentLinkStatus.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UpdatePaymentLinkStatus.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.User.UpdatePaymentLinkStatus.Response,
    AxiosResponse<ClientApi.User.UpdatePaymentLinkStatus.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/User/UpdatePaymentLinkStatus?Reference=${payload.Reference}&Disable=${payload.Disable}`,
    {},
    {
      ...config,
      ...configWithToken
    }
  ).then((res) => res.data.data);
};

export const updatePaymentLinkService = async (
  payload: ClientApi.User.UpdatePaymentLink.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UpdatePaymentLink.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.User.UpdatePaymentLink.Response,
    AxiosResponse<ClientApi.User.UpdatePaymentLink.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UpdatePaymentLink`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data.data);
};

export const deletePaymentLinkService = async (
  payload: ClientApi.User.DeletePaymentLink.Request,
  config?: RequestConfig
): Promise<ClientApi.User.DeletePaymentLink.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.delete<
    ClientApi.User.DeletePaymentLink.Response,
    AxiosResponse<ClientApi.User.DeletePaymentLink.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/User/DeletePaymentLink?PaymentLinkReference=${payload.PaymentLinkReference}`,
    {
      ...config,
      ...configWithToken
    }
  ).then((res) => res.data.data);
};

export const getPaymentLinkDetailsService = (
  payload: ClientApi.User.GetPaymentLinkDetails.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetPaymentLinkDetails.Response> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetPaymentLinkDetails.Response,
    AxiosResponse<ClientApi.User.GetPaymentLinkDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/PaymentLinkDetails?Reference=${payload.Reference}`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data);
};

export const getPaymentLinkBasicDetailsService = (
  payload: ClientApi.User.GetPaymentLinkBasicDetails.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetPaymentLinkBasicDetails.Response> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetPaymentLinkBasicDetails.Response,
    AxiosResponse<ClientApi.User.GetPaymentLinkBasicDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/PaymentLinkBasicDetails?Reference=${payload.Reference}`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data);
};

export const getRolesService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetRoles.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetRoles.Response,
    AxiosResponse<ClientApi.User.GetRoles.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/Roles`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getRoleSectionsPermissionsService = (
  payload: ClientApi.User.GetRoleSectionsPermissions.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetRoleSectionsPermissions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetRoleSectionsPermissions.Response,
    AxiosResponse<ClientApi.User.GetRoleSectionsPermissions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/RoleSectionsPermissions?RoleId=${payload.RoleId}`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getProfileDetailsService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetProfileDetails.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetProfileDetails.Response,
    AxiosResponse<ClientApi.User.GetProfileDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/ProfileDetails`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const updateProfileDetailsService = (
  payload: ClientApi.User.UpdateProfileDetails.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UpdateProfileDetails.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.put<
    ClientApi.User.UpdateProfileDetails.Response,
    AxiosResponse<ClientApi.User.UpdateProfileDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UpdateProfileDetails`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const uploadProfilePhotoService = (
  payload: ClientApi.User.UploadProfilePhoto.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UploadProfilePhoto.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const formData = new FormData();
  formData.append('DocFile', payload.DocFile);

  const configWithToken = addTokenHeader(token || '', 'formData');
  return BusinessAxiosConfig.put<
    ClientApi.User.UploadProfilePhoto.Response,
    AxiosResponse<ClientApi.User.UploadProfilePhoto.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UploadProfilePhoto`, formData, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getBusinessDetailsService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetBusinessDetails.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetBusinessDetails.Response,
    AxiosResponse<ClientApi.User.GetBusinessDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/BusinessDetails`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const updateBusinessDetailsService = (
  payload: ClientApi.User.UpdateBusinessDetails.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UpdateBusinessDetails.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.put<
    ClientApi.User.UpdateBusinessDetails.Response,
    AxiosResponse<ClientApi.User.UpdateBusinessDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UpdateBusinessDetails`, payload, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const uploadBusinessLogoService = (
  payload: ClientApi.User.UploadBusinessLogo.Request,
  config?: RequestConfig
): Promise<ClientApi.User.UploadBusinessLogo.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const formData = new FormData();
  formData.append('DocFile', payload.DocFile);

  const configWithToken = addTokenHeader(token || '', 'formData');
  return BusinessAxiosConfig.put<
    ClientApi.User.UploadBusinessLogo.Response,
    AxiosResponse<ClientApi.User.UploadBusinessLogo.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/UploadBusinessLogo`, formData, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getReceiverCustomersService = (
  payload: ClientApi.User.GetReceiverCustomers.Request,
  config?: RequestConfig
): Promise<ClientApi.User.GetReceiverCustomers.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload.startDate,
      endDate: payload.endDate
    },
    searchValue: payload.searchValue
  };

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.post<
    ClientApi.User.GetReceiverCustomers.Response,
    AxiosResponse<ClientApi.User.GetReceiverCustomers.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/ReceiverCustomers`, payload2, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getBusinessVerificationStatusService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetBusinessVerificationStatus.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetBusinessVerificationStatus.Response,
    AxiosResponse<ClientApi.User.GetBusinessVerificationStatus.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/BusinessVerificationStatus`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getPaymentLinkSummaryService = (
  config?: RequestConfig
): Promise<ClientApi.User.GetPaymentLinkSummary.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.User.GetPaymentLinkSummary.Response,
    AxiosResponse<ClientApi.User.GetPaymentLinkSummary.Response>
  >(`/api/v${BUSINESS_API_VERSION}/User/GetPaymentLinkSummary`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};
